'use client'

import { useEffect, useState, useCallback, useRef } from 'react'
import { ConnectionError } from '@/lib/types/config'
import { ConnectionErrorOverlay } from '@/components/errors/ConnectionErrorOverlay'
import { getConfig, resetConfig } from '@/lib/config'
import { useI18n } from '@/lib/hooks/use-i18n'
import { runtimeTarget } from '@/lib/runtime-target'
import { LoadingSpinner } from '@/components/common/LoadingSpinner'
import { StartupProgress } from '@/components/common/StartupProgress'
interface ConnectionGuardProps {
  children: React.ReactNode
}

function getReadableError(error: unknown): string {
  return error instanceof Error
    ? error.message
    : (typeof error === 'string' ? error : String(error))
}

export function ConnectionGuard({ children }: ConnectionGuardProps) {
  const [error, setError] = useState<ConnectionError | null>(null)
  const [connected, setConnected] = useState(false)
  const [statusMessage, setStatusMessage] = useState(
    runtimeTarget === 'desktop' ? '正在启动...' : '正在连接服务...'
  )
  const [lastErrorHint, setLastErrorHint] = useState<string | null>(null)
  const connectingRef = useRef(false)
  const { t } = useI18n()

  const loadConfig = useCallback(async () => {
    const config = await getConfig()

    if (config.dbStatus === 'offline') {
      setError({
        type: 'database-offline',
        details: {
          message: t('connection.db.offline.subtitle'),
          attemptedUrl: config.apiUrl,
        },
      })
      return false
    }

    setError(null)
    setConnected(true)
    return true
  }, [t])

  const checkConnection = useCallback(async () => {
    if (connectingRef.current) return
    connectingRef.current = true

    setError(null)
    resetConfig()

    const desktopApiUrl = process.env.NEXT_PUBLIC_DESKTOP_API_URL || 'http://127.0.0.1:5055'
    const webApiUrl = process.env.NEXT_PUBLIC_API_URL || 'http://localhost:5055'
    let lastError: unknown = null

    try {
      setStatusMessage(runtimeTarget === 'desktop' ? '正在连接本地服务...' : '正在连接服务...')
      setLastErrorHint(null)
      const ok = await loadConfig()
      if (ok) {
        connectingRef.current = false
        return
      }
    } catch (err) {
      lastError = err
      setLastErrorHint(getReadableError(err))
    }

    const errorMessage = lastError ? getReadableError(lastError) : t('connection.error.unknown')
    const attemptedUrl = runtimeTarget === 'desktop'
      ? `${desktopApiUrl}/agent-api/config`
      : `${webApiUrl}/agent-api/config`

    setError({
      type: 'api-unreachable',
      details: {
        message: t('connection.api.unreachable.subtitle'),
        technicalMessage: errorMessage,
        stack: lastError instanceof Error ? lastError.stack : undefined,
        attemptedUrl,
      },
    })
    connectingRef.current = false
  }, [loadConfig, t])

  useEffect(() => {
    // Desktop: the StartupProgress component drives readiness by polling the
    // startup-progress file (before the API is up) and then the config
    // endpoint. Skipping the eager config check here avoids a false
    // "API unreachable" error during boot.
    if (runtimeTarget === 'desktop') {
      return
    }
    checkConnection()
  }, [checkConnection])

  useEffect(() => {
    const handleKeyPress = (e: KeyboardEvent) => {
      if (error && (e.key === 'r' || e.key === 'R')) {
        e.preventDefault()
        setConnected(false)
        checkConnection()
      }
    }
    window.addEventListener('keydown', handleKeyPress)
    return () => window.removeEventListener('keydown', handleKeyPress)
  }, [error, checkConnection])

  // Fatal error — full-screen overlay (only real blocker)
  if (error) {
    return <ConnectionErrorOverlay error={error} onRetry={() => {
      setConnected(false)
      checkConnection()
    }} />
  }

  if (!connected) {
    // Desktop: show the boot progress bar with named steps (including online
    // sync). Web: keep the existing lightweight spinner.
    if (runtimeTarget === 'desktop') {
      return <StartupProgress onReady={() => setConnected(true)} />
    }

    return (
      <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-background">
        <div className="flex w-full max-w-md flex-col items-center gap-4 px-6 text-center">
          <LoadingSpinner size="lg" className="text-primary" />
          <div className="space-y-2">
            <p className="text-base font-medium text-foreground">即策AI</p>
            <p className="text-sm text-muted-foreground">{statusMessage}</p>
            {lastErrorHint && (
              <p className="text-xs text-destructive/80">
                {lastErrorHint.length > 120
                  ? lastErrorHint.slice(0, 120) + '...'
                  : lastErrorHint}
              </p>
            )}
          </div>
        </div>
      </div>
    )
  }

  return (
    <>{children}</>
  )
}
